import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getStore } from '../store/storeRef';
import { logout } from '../store/slices/authSlice';
import { API_URL } from '../config/api.config';
import toast from '../utils/toast';
import { performLogout } from './logoutService';

// --- Utility: Extract readable error message ---
function extractErrorMessage(data: unknown): string {
  if (!data) return 'Something went wrong. Please try again.';
  if (typeof data === 'string') return data;

  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === 'string') return obj.detail;
    if (typeof obj.message === 'string') return obj.message;
    if (Array.isArray(obj.errors)) return obj.errors.join(', ');
  }

  return 'Something went wrong. Please try again.';
}

// --- Utility: Handle error globally ---
function handleErrorResponse(error: AxiosError) {
  if (!error.response) {
    toast.error('Network error. Please check your connection.', 3000);
    return;
  }

  const status = error.response.status;
  const data = error.response.data as any;

  // Account deactivated by an admin - block access, tell the user, and force
  // a full logout so they land back on the Auth0 hosted login page.
  if (data?.error_type === 'account_inactive') {
    toast.error(
      (typeof data?.error === 'string' && data.error) ||
        'This account is inactive. Please contact your administrator.',
      5000
    );
    performLogout();
    return;
  }

  const message = extractErrorMessage(data);

  if (data?.errors && typeof data.errors === 'object') {
    Object.entries(data.errors).forEach(([field, messages]) => {
      if (Array.isArray(messages)) {
        messages.forEach((msg) => toast.error(`${field}: ${msg}`, 5000));
      } else if (typeof messages === 'string') {
        toast.error(`${field}: ${messages}`, 5000);
      }
    });
    return;
  }

  switch (status) {
    case 400:
      toast.error(message || 'Bad request. Please check your input.', 3000);
      break;
    case 401:
      toast.error(message || ' Unauthorized or Session expired. Please log in again.', 3000);
      // getStore().dispatch(logout());
      break;
    case 403:
      toast.error(message || 'You do not have permission to perform this action.', 3000);
      break;
    case 404:
      toast.error('The requested resource was not found.', 3000);
      break;
    case 422:
      toast.error(message || 'Validation error. Please check your input.', 3000);
      break;
    case 429:
      toast.error(message || 'Too many requests. Please slow down and try again later.', 3000);
      break;
    default:
      if (status >= 500) {
        toast.error(message || 'Server error occurred. Please try again later.', 3000);
      } else {
        toast.error(message, 3000);
      }
      break;
  }
}

// --- Axios instances ---
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const apiFormData = axios.create({
  baseURL: API_URL,
});

// --- FIXED: Properly typed request interceptor ---
const authRequestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const state = getStore().getState();
  const accessToken = state.auth.accessToken;

  if (accessToken) {
    config.headers = config.headers ?? {}; // ensure headers exist
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers?.['Content-Type'];
  }

  return config;
};

// --- Attach interceptors ---
api.interceptors.request.use(authRequestInterceptor, (error) => Promise.reject(error));
apiFormData.interceptors.request.use(authRequestInterceptor, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    handleErrorResponse(error);
    return Promise.reject(error);
  }
);

apiFormData.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    handleErrorResponse(error);
    return Promise.reject(error);
  }
);

export default api;
